import React, { useEffect, useRef } from "react";
import { toCanvas, toPng } from "html-to-image";
import defaultStripLogo from "../../Header/Images/RMB-Strip-Jaipur.png";

/**
 * RMB Jaipur – Regular Meeting Invitation Poster (3:4 ratio / 1080 x 1440 grid)
 *
 * Built on the exact 1080 x 1440 grid. SVG-native vector layout ensures 100%
 * fidelity with pixel-perfect responsive display and crisp 2x export.
 */

const NAVY = "#0f1b45";
const TITLE = "#12295a";
const CYAN = "#5ce1e6";
const GOLD_BOX = "#e0b23f";
const CARD = "#1f4b8c";
const GOLD_TXT = "#f3b52f";

const antonFont = "'Anton', 'Arial Narrow', sans-serif";
const fjallaFont = "'Fjalla One', 'Arial Narrow', sans-serif";
const sansFont = "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const FONT_URL =
  "https://fonts.googleapis.com/css2?family=Anton&family=Fjalla+One&family=Montserrat:wght@400;500;600;700;800;900&display=swap";

/**
 * Preload Google fonts in document.head so the canvas exporter renders true glyphs
 */
export async function ensureMeetingFontsLoaded() {
  if (typeof document === "undefined") return;
  if (!document.querySelector(`link[href="${FONT_URL}"]`)) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONT_URL;
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);
  }
  if (document.fonts) {
    try {
      await document.fonts.ready;
      await Promise.allSettled([
        document.fonts.load("70px 'Anton'"),
        document.fonts.load("56px 'Fjalla One'"),
        document.fonts.load("40px 'Fjalla One'"),
        document.fonts.load("25px 'Fjalla One'"),
        document.fonts.load("22px 'Fjalla One'"),
        document.fonts.load("18px 'Fjalla One'"),
        document.fonts.load("600 27px 'Montserrat'"),
        document.fonts.load("700 33px 'Montserrat'"),
        document.fonts.load("800 21px 'Montserrat'"),
      ]);
    } catch (e) {
      // font loading error ignored
    }
  }

  // Pre-cache inlined base64 Google fonts in background for instant, pixel-perfect download
  getInlinedFontsCss().catch(() => {});
}

let cachedInlinedFontCss = null;

/**
 * Fetch and embed true Google Fonts as self-contained base64 data URIs.
 * This guarantees that the SVG image parser renders true glyphs (Anton, Fjalla One, Montserrat)
 * without falling back to system Arial / Times New Roman.
 */
export async function getInlinedFontsCss() {
  if (cachedInlinedFontCss) return cachedInlinedFontCss;
  try {
    const cssRes = await fetch(FONT_URL);
    let cssText = await cssRes.text();

    const fontUrls = (cssText.match(/https:\/\/fonts\.gstatic\.com\/[^\s"')]+/g) || []);
    const uniqueUrls = [...new Set(fontUrls)];

    const map = {};
    await Promise.all(
      uniqueUrls.map(async (url) => {
        try {
          const res = await fetch(url);
          const blob = await res.blob();
          const dataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve("");
            reader.readAsDataURL(blob);
          });
          if (dataUrl && typeof dataUrl === "string") {
            map[url] = dataUrl;
          }
        } catch (e) {
          console.warn("Could not inline font binary:", url, e);
        }
      })
    );

    for (const [url, dataUrl] of Object.entries(map)) {
      if (dataUrl && dataUrl.startsWith("data:")) {
        cssText = cssText.split(url).join(dataUrl);
      }
    }

    cachedInlinedFontCss = cssText;
    return cssText;
  } catch (err) {
    console.warn("Could not fetch and inline Google Fonts CSS:", err);
    return "";
  }
}

/**
 * Converts an image URL (including cross-origin member photos) to a base64 data URL
 * so that SVG export has zero external network dependencies.
 */
async function toDataUrl(url) {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("data:")) return url;

  // Cache-busting query parameter prevents Chrome CORS cache poisoning
  const bustUrl = url.includes("?")
    ? `${url}&_t=${Date.now()}`
    : `${url}?_t=${Date.now()}`;

  // 1. Try fetch with CORS & cache-buster
  try {
    const res = await fetch(bustUrl, { mode: "cors" });
    if (res.ok) {
      const blob = await res.blob();
      const dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve("");
        reader.readAsDataURL(blob);
      });
      if (dataUrl && typeof dataUrl === "string" && dataUrl.startsWith("data:")) {
        return dataUrl;
      }
    }
  } catch (e) {
    // continue
  }

  // 2. Try Image with crossOrigin = "anonymous"
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((res, rej) => {
      img.onload = res;
      img.onerror = rej;
      img.src = bustUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width || 200;
    canvas.height = img.naturalHeight || img.height || 200;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    const canvasData = canvas.toDataURL("image/png");
    if (canvasData && canvasData.startsWith("data:")) {
      return canvasData;
    }
  } catch (err1) {
    // continue
  }

  // 3. Fallback to original URL fetch
  try {
    const res = await fetch(url, { mode: "cors" });
    if (res.ok) {
      const blob = await res.blob();
      const dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve("");
        reader.readAsDataURL(blob);
      });
      if (dataUrl && typeof dataUrl === "string" && dataUrl.startsWith("data:")) {
        return dataUrl;
      }
    }
  } catch (err2) {
    console.warn("Could not inline image URL to data URL:", url, err2);
  }

  return "";
}

/**
 * Pixel-perfect Canvas Exporter for RmbMeetingPoster
 * Renders at 2x sharpness (2160 x 2880) matching on-screen poster 100%.
 *
 * Uses direct SVG rasterization with self-contained base64 fonts & images,
 * ensuring zero font distortion, exact layout alignment, and no CSS-scale mismatch.
 */
export async function generateMeetingPosterCanvas(element) {
  let el = element;
  if (!el) {
    el = document.querySelector(".rmb-meeting-poster");
  }
  if (!el) {
    throw new Error("Meeting poster element not found");
  }

  // Find the exact SVG element inside el, or el itself if it's the SVG
  const svgEl = el.tagName && el.tagName.toLowerCase() === "svg" ? el : el.querySelector("svg");
  if (!svgEl) {
    throw new Error("SVG element not found inside meeting poster");
  }

  // Small delay to ensure any live image rendering or state update has painted
  await new Promise((r) => setTimeout(r, 60));

  try {
    // Clone the on-screen SVG master
    const svgClone = svgEl.cloneNode(true);
    svgClone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    svgClone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
    svgClone.setAttribute("width", "1080");
    svgClone.setAttribute("height", "1440");
    svgClone.setAttribute("viewBox", "0 0 1080 1440");

    // 1. Inline all external images (logos, photos, QR) to base64
    const images = svgClone.querySelectorAll("image");
    await Promise.all(
      Array.from(images).map(async (img) => {
        const href = img.getAttribute("href") || img.getAttribute("xlink:href");
        if (href && !href.startsWith("data:")) {
          const dataUrl = await toDataUrl(href);
          if (dataUrl && dataUrl.startsWith("data:")) {
            img.setAttribute("href", dataUrl);
            img.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", dataUrl);
          }
        }
      })
    );

    // 2. Fetch and embed true base64 Google Fonts
    const fontCss = await getInlinedFontsCss();
    let styleEl = svgClone.querySelector("defs style") || svgClone.querySelector("style");
    if (!styleEl) {
      let defs = svgClone.querySelector("defs");
      if (!defs) {
        defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
        svgClone.insertBefore(defs, svgClone.firstChild);
      }
      styleEl = document.createElementNS("http://www.w3.org/2000/svg", "style");
      defs.appendChild(styleEl);
    }
    if (fontCss) {
      styleEl.textContent = fontCss;
    }

    // 3. Serialize self-contained SVG to Blob URL
    const svgString = new XMLSerializer().serializeToString(svgClone);
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const blobUrl = URL.createObjectURL(svgBlob);

    // 4. Render to full 2x high-resolution canvas (2160 x 2880) matching on-screen poster 100%
    const targetW = 2160;
    const targetH = 2880;
    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");

    // Crisp white background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, targetW, targetH);

    const img = new Image();
    await new Promise((res, rej) => {
      img.onload = res;
      img.onerror = (e) => rej(new Error("Failed to rasterize poster SVG to canvas"));
      img.src = blobUrl;
    });

    ctx.drawImage(img, 0, 0, targetW, targetH);
    URL.revokeObjectURL(blobUrl);

    return canvas;
  } catch (err) {
    console.warn("Direct SVG rasterization failed, falling back to toCanvas:", err);
    // Fallback: html-to-image
    const rect = el.getBoundingClientRect();
    const currentWidth = rect.width || 540;
    const pixelRatio = (1080 / currentWidth) * 2;

    try {
      const fallbackCanvas = await toCanvas(el, {
        pixelRatio: Math.max(pixelRatio, 2),
        cacheBust: true,
        backgroundColor: "#ffffff",
      });
      return fallbackCanvas;
    } catch (toCanvasErr) {
      console.warn("toCanvas fallback failed, trying toPng:", toCanvasErr);
      const dataUrl = await toPng(el, {
        pixelRatio: Math.max(pixelRatio, 2),
        cacheBust: true,
        backgroundColor: "#ffffff",
      });
      const img = new Image();
      await new Promise((res, rej) => {
        img.onload = res;
        img.onerror = rej;
        img.src = dataUrl;
      });
      const fallbackCanvas = document.createElement("canvas");
      fallbackCanvas.width = img.width;
      fallbackCanvas.height = img.height;
      const ctx = fallbackCanvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0);
      }
      return fallbackCanvas;
    }
  }
}

/**
 * Download Meeting Poster as PNG directly
 */
export async function downloadMeetingPoster(elementOrProps, filename) {
  const canvas = await generateMeetingPosterCanvas(elementOrProps);
  const dataUrl = canvas.toDataURL("image/png");
  const link = document.createElement("a");
  link.download = filename || "RMB_Meeting_Poster.png";
  link.href = dataUrl;
  link.click();
  return dataUrl;
}

/* Gold tile icons ---------------------------------------------------- */
const Tile = ({ x, y, w = 84, h = 84, children }) => (
  <g transform={`translate(${x} ${y})`}>
    <rect x="3" y="4" width={w} height={h} rx="12" fill="#00000030" />
    <rect width={w} height={h} rx="12" fill="url(#tileGrad)" stroke="#fbe29a" strokeWidth="2" />
    {children}
  </g>
);

const CalendarIcon = () => (
  <>
    <rect x="16" y="20" width="52" height="48" rx="7" fill="#4a3a14" />
    <rect x="16" y="20" width="52" height="13" rx="6" fill="#2f2408" />
    <rect x="25" y="12" width="6" height="14" rx="3" fill="#2f2408" />
    <rect x="53" y="12" width="6" height="14" rx="3" fill="#2f2408" />
    {[0, 1, 2].flatMap((r) =>
      [0, 1, 2, 3].map((c) => (
        <rect key={`${r}${c}`} x={22 + c * 11} y={38 + r * 9} width="7" height="6" rx="1.5" fill="#f1cf7d" />
      ))
    )}
  </>
);

const ClockIcon = () => (
  <>
    <circle cx="42" cy="42" r="28" fill="#f8dc8c" stroke="#3a2c0c" strokeWidth="4" />
    <path d="M42 24v19l13 8" stroke="#3a2c0c" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <circle cx="42" cy="42" r="3" fill="#3a2c0c" />
  </>
);

const PinIcon = () => (
  <>
    <path d="M14 66l28-12 28 12-28 12z" fill="none" stroke="#3a2c0c" strokeWidth="4" strokeLinejoin="round" />
    <path d="M42 56C30 44 26 38 26 31a16 16 0 0 1 32 0c0 7-4 13-16 25z" fill="#3a2c0c" />
    <circle cx="42" cy="31" r="6" fill="#f8dc8c" />
  </>
);

/* Fallback vector QR ------------------------------------------------- */
const FakeQr = () => {
  const n = 25;
  const cells = [];
  let s = 7;
  const rnd = () => ((s = (s * 9301 + 49297) % 233280) / 233280);
  const finder = (r, c) =>
    (r < 7 && c < 7) || (r < 7 && c >= n - 7) || (r >= n - 7 && c < 7);
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (finder(r, c)) continue;
      if (rnd() > 0.5) cells.push(<rect key={`${r}-${c}`} x={c} y={r} width="1" height="1" fill="#111" />);
    }
  }
  const Finder = ({ x, y }) => (
    <g transform={`translate(${x} ${y})`}>
      <rect width="7" height="7" fill="#111" />
      <rect x="1" y="1" width="5" height="5" fill="#fff" />
      <rect x="2" y="2" width="3" height="3" fill="#111" />
    </g>
  );
  return (
    <g>
      {cells}
      <Finder x={0} y={0} />
      <Finder x={n - 7} y={0} />
      <Finder x={0} y={n - 7} />
    </g>
  );
};

/* SVG Text helper with crisp typography, natural kerning, and automatic overflow clamp */
const SvgText = ({
  x,
  cy,
  size,
  font = sansFont,
  color = TITLE,
  weight = "normal",
  textAnchor = "start",
  letterSpacing,
  maxW,
  children,
}) => {
  if (!children && children !== 0) return null;
  const str = typeof children === "string" || typeof children === "number" ? String(children) : "";
  const isCond = typeof font === "string" && (font.includes("Fjalla") || font.includes("Anton"));
  const charRatio = isCond ? 0.46 : 0.55;
  const estWidth = str ? str.length * size * charRatio : 0;
  // Only apply textLength when the text actually exceeds maxW so short text is never stretched
  const shouldClamp = Boolean(maxW && estWidth > maxW);

  return (
    <text
      x={x}
      y={cy}
      dominantBaseline="central"
      textAnchor={textAnchor}
      fill={color}
      fontFamily={font}
      fontSize={size}
      fontWeight={weight}
      letterSpacing={letterSpacing}
      {...(shouldClamp ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {})}
    >
      {children}
    </text>
  );
};

/**
 * Dynamic font sizing for Fjalla One condensed uppercase text
 * Fjalla One character width ratio is ~0.46 * fontSize
 */
function getFjallaFontSize(text, baseSize = 25, minSize = 13, maxW = 244) {
  const len = (text || "").length;
  if (!len) return baseSize;
  const estWidth = len * baseSize * 0.46;
  if (estWidth <= maxW) return baseSize;
  const fitted = maxW / (len * 0.46);
  return Math.max(minSize, Math.min(baseSize, Math.round(fitted * 10) / 10));
}

/**
 * Dynamic font sizing for Montserrat company text
 * Montserrat character width ratio is ~0.55 * fontSize
 */
function getCompanyFontSize(text, baseSize = 17, minSize = 12, maxW = 244) {
  const len = (text || "").length;
  if (!len) return baseSize;
  const estWidth = len * baseSize * 0.55;
  if (estWidth <= maxW) return baseSize;
  const fitted = maxW / (len * 0.55);
  return Math.max(minSize, Math.min(baseSize, Math.round(fitted * 10) / 10));
}

/**
 * Splits presenter / slot names intelligently so they never overflow the card or window.
 * - Single-line if <= maxOneLineLen (default 24 characters).
 * - Multi-line (2 lines) if longer, balancing words while keeping prefixes like "RMBIAN:" or "DR."
 *   cohesive and avoiding awkward single-word orphans.
 */
function formatSlotName(rawName, maxOneLineLen = 24) {
  const text = String(rawName || "").trim();
  if (!text) return { lines: [""], isMultiLine: false };

  // Explicit newline provided
  if (text.includes("\n")) {
    const parts = text.split("\n").map((s) => s.trim()).filter(Boolean);
    return { lines: parts.slice(0, 2), isMultiLine: parts.length > 1 };
  }

  // Short enough to fit on 1 line cleanly
  if (text.length <= maxOneLineLen) {
    return { lines: [text], isMultiLine: false };
  }

  const words = text.split(/\s+/);
  if (words.length <= 1) {
    // Single unbroken word
    return { lines: [text], isMultiLine: false };
  }

  let bestSplit = 1;
  let bestScore = Infinity;

  for (let i = 1; i < words.length; i++) {
    const l1 = words.slice(0, i).join(" ");
    const l2 = words.slice(i).join(" ");
    const maxLen = Math.max(l1.length, l2.length);
    const diff = Math.abs(l1.length - l2.length);

    let penalty = 0;
    // Don't leave 'RMBIAN:' or 'RMBIAN' isolated on line 1 if there are 3+ words
    if (words.length > 2 && i === 1 && /^RMBIAN:?$/i.test(words[0])) {
      penalty += 18;
    }
    // Discourage very short lines
    if (l1.length < 7) penalty += 12;
    if (l2.length < 5) penalty += 12;

    const score = maxLen * 10 + diff + penalty;
    if (score < bestScore) {
      bestScore = score;
      bestSplit = i;
    }
  }

  const line1 = words.slice(0, bestSplit).join(" ");
  const line2 = words.slice(bestSplit).join(" ");
  return { lines: [line1, line2], isMultiLine: true };
}

/**
 * Component to render presenter slot card content (name + company)
 * Dynamically adjusts single-line vs multi-line, auto-scales font sizes,
 * and clamps to maxW so it never exceeds the card or window.
 */
const SlotCardContent = ({ cardY, cardH = 128, centerX = 914, name, company }) => {
  const { lines, isMultiLine } = formatSlotName(name, 24);
  const comp = (company || "").trim();
  const maxW = 244;

  if (isMultiLine) {
    const s1 = getFjallaFontSize(lines[0], 21, 14, maxW);
    const s2 = getFjallaFontSize(lines[1], 21, 14, maxW);
    const cSize = getCompanyFontSize(comp, 15, 12, maxW);

    const cy1 = comp ? cardY + 34 : cardY + 49;
    const cy2 = comp ? cardY + 59 : cardY + 75;
    const cyComp = cardY + 92;

    return (
      <g>
        <SvgText x={centerX} cy={cy1} size={s1} font={fjallaFont} color={GOLD_TXT} textAnchor="middle" maxW={maxW}>
          {lines[0]}
        </SvgText>
        <SvgText x={centerX} cy={cy2} size={s2} font={fjallaFont} color={GOLD_TXT} textAnchor="middle" maxW={maxW}>
          {lines[1]}
        </SvgText>
        {comp ? (
          <SvgText x={centerX} cy={cyComp} size={cSize} font={sansFont} color="#ffffff" weight="600" textAnchor="middle" maxW={maxW}>
            {comp}
          </SvgText>
        ) : null}
      </g>
    );
  }

  // Single line name
  const nameSize = getFjallaFontSize(lines[0], 25, 14, maxW);
  const cSize = getCompanyFontSize(comp, 17, 12, maxW);
  const cyName = comp ? cardY + 49 : cardY + cardH / 2;
  const cyComp = cardY + 85;

  return (
    <g>
      <SvgText x={centerX} cy={cyName} size={nameSize} font={fjallaFont} color={GOLD_TXT} textAnchor="middle" maxW={maxW}>
        {lines[0]}
      </SvgText>
      {comp ? (
        <SvgText x={centerX} cy={cyComp} size={cSize} font={sansFont} color="#ffffff" weight="600" textAnchor="middle" maxW={maxW}>
          {comp}
        </SvgText>
      ) : null}
    </g>
  );
};

const RmbMeetingPoster = React.forwardRef((props, ref) => {
  const {
    logo = defaultStripLogo,
    qr = "",
    qrCaption = "merchant1334199.augp@aubank",
    title = ["RMB JAIPUR'S 127th", "REGULAR MEETING"],
    date = { day: "26th SEPT.", year: "2026", weekday: "SATURDAY" },
    time = ["8:00 AM", "onwards"],
    venue = ["The Fern Jaipur, Series by", "Marriott, Tonk Road"],
    slots = [
      { heading: ["BUSINESS", "PRESENTATION"], name: "RMBIAN: VAIBHAV BAGLA", company: "Natural Consume Care", photo: "" },
      { heading: ["EDUCATION", "Slot"], name: "RMBIAN: KSHITIJ PRASHAD", company: "AAKS Global Associates", photo: "" },
    ],
    dressCode = "Formal",
    charges = "REGISTRATION CHARGES RS. 800/- FOR VISTORS",
    note = "( Meeting followed by Breakfast )",
    rsvp = [
      "+91 9829055889  RMBian: Siddharth Makharia",
      "+91 9024111753  RMBian: Gaurav Mathur",
      "+91 7301020313  RMBian: Ashish Bhardwaj",
      "+91 9557969530  RMBian: Dr. Megha Sharma",
    ],
    website = "https://rmbjaipur.com/",
    className = "",
    style = {},
  } = props;

  const containerRef = useRef(null);

  // Preload fonts on mount
  useEffect(() => {
    ensureMeetingFontsLoaded();
  }, []);

  // Attach canvas generators directly to ref DOM node so callers can call getPosterCanvas()
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    el.getPosterCanvas = () => generateMeetingPosterCanvas(el);
    el.getPosterDataUrl = async () => {
      const c = await el.getPosterCanvas();
      return c.toDataURL("image/png");
    };
  }, [logo, qr, qrCaption, title, date, time, venue, slots, dressCode, charges, note, rsvp, website]);

  const setCombinedRef = (node) => {
    containerRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  // Title lines normalization
  const titleLines = Array.isArray(title)
    ? title
    : String(title || "RMB JAIPUR'S 127th\nREGULAR MEETING").split("\n");
  const title0 = titleLines[0] || "RMB JAIPUR'S 127th";
  const title1 = titleLines[1] || "REGULAR MEETING";

  // Date normalization
  let dDay = "26th SEPT.";
  let dYear = "2026";
  let dWeekday = "SATURDAY";
  if (date && typeof date === "object" && date.day) {
    dDay = date.day;
    dYear = date.year || "2026";
    dWeekday = date.weekday || "SATURDAY";
  } else if (typeof date === "string" && date) {
    try {
      const parsed = new Date(date.includes(" ") && !date.includes("T") ? date.replace(" ", "T") : date);
      if (!isNaN(parsed.getTime())) {
        const day = parsed.getDate();
        const s = ["th", "st", "nd", "rd"];
        const v = day % 100;
        const ord = day + (s[(v - 20) % 10] || s[v] || s[0]);
        const months = ["JAN.", "FEB.", "MAR.", "APR.", "MAY", "JUNE", "JULY", "AUG.", "SEPT.", "OCT.", "NOV.", "DEC."];
        const days = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
        dDay = `${ord} ${months[parsed.getMonth()]}`;
        dYear = String(parsed.getFullYear());
        dWeekday = days[parsed.getDay()];
      }
    } catch {}
  }

  // Time lines normalization
  const timeLines = Array.isArray(time)
    ? time
    : String(time || "8:00 AM\nonwards").split("\n");
  const time0 = timeLines[0] || "8:00 AM";
  const time1 = timeLines[1] || "onwards";

  // Full address lines normalization – preserves all lines/parts of the address
  let venueLines = [];
  if (Array.isArray(venue) && venue.length > 0) {
    venueLines = venue.map((v) => String(v || "").trim()).filter(Boolean);
  } else if (typeof venue === "string" && venue.trim()) {
    const raw = venue.trim();
    if (raw.includes("\n")) {
      venueLines = raw.split("\n").map((s) => s.trim()).filter(Boolean);
    } else if (raw.includes(",")) {
      venueLines = raw.split(",").map((s) => s.trim()).filter(Boolean);
    } else {
      venueLines = [raw];
    }
  }

  if (venueLines.length === 0) {
    venueLines = ["The Fern Jaipur, Series by", "Marriott, Tonk Road"];
  }

  // Slots normalization
  const s0 = slots?.[0] || { heading: ["BUSINESS", "PRESENTATION"], name: "", company: "", photo: "" };
  const s1 = slots?.[1] || { heading: ["EDUCATION", "Slot"], name: "", company: "", photo: "" };

  const s0Head = Array.isArray(s0.heading) ? s0.heading : [s0.heading || "BUSINESS", "PRESENTATION"];
  const s1Head = Array.isArray(s1.heading) ? s1.heading : [s1.heading || "EDUCATION", "Slot"];

  // Helper to format company name up to 4 words cleanly
  const formatCompany = (val, fallback = "") => {
    const raw = (val || fallback || "").trim();
    if (!raw) return "";
    const words = raw.split(/\s+/);
    if (words.length <= 4) return raw;
    return words.slice(0, 4).join(" ");
  };

  const s0Company = formatCompany(s0.company, "Natural Consume Care");
  const s1Company = formatCompany(s1.company, "AAKS Global Associates");

  return (
    <div
      ref={setCombinedRef}
      className={`rmb-meeting-poster relative overflow-hidden bg-white shadow-2xl rounded-2xl select-none ${className}`}
      style={{
        width: "100%",
        maxWidth: "560px",
        aspectRatio: "1080 / 1440",
        margin: "0 auto",
        ...style,
      }}
    >
      <svg
        viewBox="0 0 1080 1440"
        className="w-full h-full block"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
      >
        <defs>
          {/* Web fonts (Anton, Fjalla One, Montserrat with all required weights) */}
          <style>{`
            @import url('${FONT_URL}');
          `}</style>

          <linearGradient id="tileGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#f6cf55" />
            <stop offset="1" stopColor="#c8932a" />
          </linearGradient>
          <linearGradient id="bandGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#d9d94a" stopOpacity="0" />
            <stop offset="0.18" stopColor="#dcd94a" stopOpacity="0.95" />
            <stop offset="0.6" stopColor="#c9df6c" stopOpacity="0.9" />
            <stop offset="1" stopColor="#c9df6c" stopOpacity="0" />
          </linearGradient>

          <linearGradient id="foldTop" gradientUnits="userSpaceOnUse" x1="0" y1="359" x2="0" y2="431">
            <stop offset="0" stopColor="#3a9494" />
            <stop offset="0.45" stopColor="#2a8083" />
            <stop offset="0.8" stopColor="#12474a" />
            <stop offset="1" stopColor="#0a3234" />
          </linearGradient>
          <linearGradient id="foldBottom" gradientUnits="userSpaceOnUse" x1="0" y1="1077" x2="0" y2="1141">
            <stop offset="0" stopColor="#16494c" />
            <stop offset="0.25" stopColor="#11393c" />
            <stop offset="0.5" stopColor="#1f6968" />
            <stop offset="0.7" stopColor="#2f8a8f" />
            <stop offset="1" stopColor="#359395" />
          </linearGradient>

          <filter id="softBlur" x="-10%" y="-60%" width="120%" height="220%">
            <feGaussianBlur stdDeviation="4" />
          </filter>

          {/* Photo clip paths */}
          <clipPath id="circlePhoto0">
            <circle cx="680" cy="638" r="83.5" />
          </clipPath>
          <clipPath id="circlePhoto1">
            <circle cx="680" cy="898" r="83.5" />
          </clipPath>
        </defs>

        {/* ───────── LAYER 1: Navy background bar, Cyan lines ───────── */}
        <rect x="704" y="0" width="277" height="1440" fill={NAVY} />

        {/* Cyan horizontal line clusters */}
        {[
          [718, 965, 178], [780, 965, 216], [842, 965, 253],
          [712, 835, 1185], [712, 897, 1222], [712, 960, 1259],
        ].map(([a, b, y]) => (
          <rect key={y} x={a} y={y - 3} width={b - a} height="6" fill="#4cc9ee" />
        ))}

        {/* Ribbon folds */}
        {/* Ribbon body */}
        <path d="M648 430 L1053 430 L1053 1077 L642 1077 L470 748 Q452 718 470 690 Z" fill={CYAN} />

        {/* Light cyan patches beside the folds */}
        <path d="M1010 428 C1032 418 1050 404 1053 393 L1053 431 L1010 431 Z" fill="#4fd0d8" />
        <path d="M1020 1077 C1040 1082 1052 1096 1053 1109 L1053 1077 Z" fill="#48ccce" />

        {/* Top fold (dark curl) */}
        <path
          d="M981 359 C1010 364 1035 369 1046 381 Q1052 388 1053 393 C1050 404 1032 418 1010 428 L1002 431 L981 431 Z"
          fill="url(#foldTop)"
        />

        {/* Bottom fold (dark curl) */}
        <path
          d="M981 1077 L1020 1077 C1040 1082 1052 1096 1053 1109 C1046 1122 1010 1138 981 1141 Z"
          fill="url(#foldBottom)"
        />

        {/* Yellow heading glow bands */}
        <g filter="url(#softBlur)">
          <rect x="640" y="446" width="398" height="48" rx="10" fill="url(#bandGrad)" />
          <rect x="655" y="506" width="395" height="52" rx="10" fill="url(#bandGrad)" />
          <rect x="645" y="712" width="395" height="54" rx="10" fill="url(#bandGrad)" />
          <rect x="660" y="773" width="385" height="52" rx="10" fill="url(#bandGrad)" />
        </g>

        {/* Left gold tiles with icons */}
        <Tile x={68} y={520}><CalendarIcon /></Tile>
        <Tile x={68} y={690}><ClockIcon /></Tile>
        <Tile x={65} y={853} w={88} h={90}><g transform="translate(2 3)"><PinIcon /></g></Tile>

        {/* Date underline + divider */}
        <rect x="165" y="566" width="285" height="3" fill={GOLD_TXT} />
        <rect x="270" y="572" width="3" height="32" fill={GOLD_TXT} />

        {/* ───────── LAYER 2: Cards & Photo Circles ───────── */}
        {/* Presentation slot 0 card */}
        <rect x="778" y="571" width="272" height="128" rx="24" fill={CARD} />
        {/* Presentation slot 1 card */}
        <rect x="778" y="849" width="272" height="128" rx="24" fill={CARD} />

        {/* Slot 0 Circle Photo */}
        <circle cx="680" cy="638" r="83.5" fill="#cbd5e1" stroke="#dfe6ee" strokeWidth="6" />
        {s0.photo ? (
          <image
            href={s0.photo}
            x="596.5"
            y="554.5"
            width="167"
            height="167"
            preserveAspectRatio="xMidYMid slice"
            clipPath="url(#circlePhoto0)"
          />
        ) : (
          <g transform="translate(642, 600) scale(3.2)">
            <path
              d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
              fill="#64748b"
            />
          </g>
        )}
        <circle cx="680" cy="638" r="83.5" fill="none" stroke="#dfe6ee" strokeWidth="6" />

        {/* Slot 1 Circle Photo */}
        <circle cx="680" cy="898" r="83.5" fill="#cbd5e1" stroke="#dfe6ee" strokeWidth="6" />
        {s1.photo ? (
          <image
            href={s1.photo}
            x="596.5"
            y="814.5"
            width="167"
            height="167"
            preserveAspectRatio="xMidYMid slice"
            clipPath="url(#circlePhoto1)"
          />
        ) : (
          <g transform="translate(642, 860) scale(3.2)">
            <path
              d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
              fill="#64748b"
            />
          </g>
        )}
        <circle cx="680" cy="898" r="83.5" fill="none" stroke="#dfe6ee" strokeWidth="6" />

        {/* Dress code folded tag */}
        <g transform="rotate(-7 805 1020)">
          {/* corner bracket – hugs the top-right corner */}
          <path d="M874 994 L909 994 L909 1018" fill="none" stroke={NAVY} strokeWidth="2.5" strokeLinejoin="miter" />
          <polygon points="708,1000 903,1000 903,1042 708,1042" fill={NAVY} />
          <polygon points="708,1042 730,1042 730,1072" fill="#0a1233" />
          <text x="717" y="1022" dominantBaseline="central" fontFamily={sansFont} fontSize="17" fontWeight="700">
            <tspan fill={GOLD_TXT}>Dress Code: </tspan>
            <tspan fill="#ffffff" fontWeight="600">{dressCode}</tspan>
          </text>
        </g>

        {/* ───────── LAYER 3: Rotary / RMB Jaipur Logo ───────── */}
        {logo ? (
          <image
            href={logo}
            x="50"
            y="65"
            width="605"
            height="120"
            preserveAspectRatio="xMinYMid meet"
          />
        ) : (
          <text x="50" y="125" dominantBaseline="central" fontFamily={sansFont} fontSize="34" fontWeight="bold" fill={TITLE}>
            ROTARY | RMB JAIPUR
          </text>
        )}

        {/* ───────── Left Section Headings & Text ───────── */}
        <SvgText x={65} cy={278} size={70} font={antonFont} color={TITLE}>
          {title0}
        </SvgText>
        <SvgText x={65} cy={361} size={70} font={antonFont} color={TITLE}>
          {title1}
        </SvgText>

        {/* Date Texts */}
        <SvgText x={181} cy={526} size={74} font={antonFont} color={TITLE}>
          {dDay}
        </SvgText>
        <SvgText x={182} cy={587} size={33} font={sansFont} color={TITLE} weight="700">
          {dYear}
        </SvgText>
        <SvgText x={282} cy={587} size={33} font={sansFont} color={TITLE} weight="700">
          {dWeekday}
        </SvgText>

        {/* Time Texts */}
        <SvgText x={180} cy={710} size={56} font={fjallaFont} color={TITLE}>
          {time0}
        </SvgText>
        <SvgText x={180} cy={762} size={56} font={fjallaFont} color={TITLE}>
          {time1}
        </SvgText>

        {/* Venue Texts – Full Address */}
        {venueLines.map((line, idx) => {
          const total = venueLines.length;
          const fontSize = total <= 2 ? 34 : total === 3 ? 28 : 23;
          const lineGap = total <= 2 ? 40 : total === 3 ? 34 : 28;
          const startY = total <= 2 ? 880 : total === 3 ? 870 : 864;
          return (
            <SvgText
              key={idx}
              x={162}
              cy={startY + idx * lineGap}
              size={fontSize}
              font={fjallaFont}
              color={TITLE}
            >
              {line}
            </SvgText>
          );
        })}

        {/* ───────── Right Ribbon Texts ───────── */}
        {/* Slot 0 Header & Details */}
        <SvgText x={825} cy={472} size={40} font={fjallaFont} color={TITLE} textAnchor="middle" maxW={370}>
          {s0Head[0]}
        </SvgText>
        <SvgText x={825} cy={532} size={40} font={fjallaFont} color={TITLE} textAnchor="middle" maxW={370}>
          {s0Head[1]}
        </SvgText>
        <SlotCardContent
          cardY={571}
          cardH={128}
          centerX={914}
          name={s0.name || "RMBIAN: VAIBHAV BAGLA"}
          company={s0Company}
        />

        {/* Slot 1 Header & Details */}
        <SvgText x={825} cy={738} size={40} font={fjallaFont} color={TITLE} textAnchor="middle" maxW={370}>
          {s1Head[0]}
        </SvgText>
        <SvgText x={825} cy={800} size={40} font={fjallaFont} color={TITLE} textAnchor="middle" maxW={370}>
          {s1Head[1]}
        </SvgText>
        <SlotCardContent
          cardY={849}
          cardH={128}
          centerX={914}
          name={s1.name || "RMBIAN: KSHITIJ PRASHAD"}
          company={s1Company}
        />

        {/* ───────── Registration Gold Box ───────── */}
        <rect x={26} y={1029} width={591} height={181} rx={24} fill={GOLD_BOX} />
        <SvgText x={321.5} cy={1068} size={charges && charges.length > 42 ? 18 : 21} font={sansFont} color={TITLE} weight="800" textAnchor="middle" maxW={560}>
          {charges}
        </SvgText>
        <SvgText x={255} cy={1145} size={note && note.length > 40 ? 19 : 22} font={sansFont} color={TITLE} weight="600" textAnchor="middle" maxW={430}>
          {note}
        </SvgText>

        {/* Payment QR Box */}
        <g transform="translate(495, 1095)">
          <rect x="0" y="0" width="101" height="101" rx="3" fill="#ffffff" />
          {qr ? (
            <image href={qr} x="13" y="9" width="75" height="75" preserveAspectRatio="xMidYMid meet" />
          ) : (
            <g transform="translate(13, 9) scale(3)">
              <FakeQr />
            </g>
          )}
          <text
            x="8"
            y="93"
            dominantBaseline="central"
            textLength="85"
            lengthAdjust="spacing"
            fontFamily={sansFont}
            fontSize="5"
            fontWeight="bold"
            fill="#111"
          >
            {qrCaption}
          </text>
        </g>

        {/* ───────── RSVP Section ───────── */}
        <text
          x={43}
          y={1297}
          dominantBaseline="central"
          textAnchor="middle"
          transform="rotate(-90 43 1297)"
          fontFamily={sansFont}
          fontSize="31"
          fontWeight="800"
          fill={TITLE}
          letterSpacing="0.05em"
        >
          RSVP
        </text>

        {rsvp.map((line, i) => {
          const rText = String(line || "").trim();
          const rSize = rText.length > 38 ? 21 : rText.length > 32 ? 23.5 : 27;
          return (
            <SvgText
              key={i}
              x={71}
              cy={1249 + i * 32.7}
              size={rSize}
              font={sansFont}
              color={TITLE}
              weight="600"
              maxW={535}
            >
              {rText}
            </SvgText>
          );
        })}

        {/* ───────── Website Gold Bar ───────── */}
        <rect x={26} y={1376} width={591} height={39} rx={8} fill={GOLD_BOX} />
        <SvgText x={321.5} cy={1395} size={24} font={sansFont} color={TITLE} weight="700" textAnchor="middle" maxW={560}>
          {website}
        </SvgText>
      </svg>
    </div>
  );
});

export default RmbMeetingPoster;